import React, { useEffect, useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardMedia,
    CardContent,
    CardActions,
    Button,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Alert,
    Avatar,
    useTheme,
    useMediaQuery,
    IconButton,
    Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import ShareIcon from '@mui/icons-material/Share';
import HistoryIcon from '@mui/icons-material/History';
import axios from 'axios';
import { BaseURL, BucketURL } from '../../ApiSetup/Api';
import sessionStoreUtil from '../../ApiSetup/Localstorage';

const MemberPostGenerator = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();

    const [categories, setCategories] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [myPosts, setMyPosts] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [generatedPost, setGeneratedPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [userInfo, setUserInfo] = useState(null);

    const getAuthHeaders = () => {
        const token = sessionStoreUtil.getDataSessionStore("token");
        return { Authorization: `Bearer ${token}` };
    };

    useEffect(() => {
        const token = sessionStoreUtil.getDataSessionStore("token");
        if (!token) {
            navigate('/admin/login');
            return;
        }
        fetchUserInfo();
        fetchCategories();
        fetchMyPosts();
    }, []);

    const fetchUserInfo = async () => {
        try {
            const response = await axios.get(`${BaseURL}user/user_info`, {
                headers: getAuthHeaders()
            });
            if (response?.data?.data) {
                setUserInfo(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching user info:', error);
        }
    };

    const fetchCategories = async () => {
        setLoading(true);
        try {
            const response = await axios.post(
                `${BaseURL}user/post-generator/categories`,
                { platform: 'website' },
                { headers: getAuthHeaders() }
            );
            if (response?.data?.success) {
                setCategories(response?.data?.data || []);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchTemplates = async (categoryId) => {
        setLoadingTemplates(true);
        try {
            const response = await axios.get(
                `${BaseURL}user/post-generator/templates/${categoryId}`,
                { headers: getAuthHeaders() }
            );
            if (response?.data?.success) {
                setTemplates(response?.data?.data || []);
            }
        } catch (error) {
            console.error('Error fetching templates:', error);
        } finally {
            setLoadingTemplates(false);
        }
    };

    const fetchMyPosts = async () => {
        try {
            const response = await axios.post(
                `${BaseURL}user/post-generator/my-posts`,
                { page: 1, limit: 10 },
                { headers: getAuthHeaders() }
            );
            if (response?.data?.success) {
                setMyPosts(response?.data?.data || []);
            }
        } catch (error) {
            console.error('Error fetching my posts:', error);
        }
    };

    const handleCategoryClick = (category) => {
        setSelectedCategory(category);
        fetchTemplates(category._id);
    };

    const handleTemplateClick = (template) => {
        setSelectedTemplate(template);
        setPreviewOpen(true);
    };

    const handleGenerate = async () => {
        if (!selectedTemplate) return;
        
        setGenerating(true);
        try {
            const response = await axios.post(
                `${BaseURL}user/post-generator/generate`,
                { templateId: selectedTemplate._id, source: 'website' },
                { headers: getAuthHeaders() }
            );
            
            if (response?.data?.success) {
                setGeneratedPost(response.data.data);
                fetchMyPosts();
            }
        } catch (error) {
            console.error('Error generating post:', error);
        } finally {
            setGenerating(false);
        }
    };

    const handleDownload = async (postId) => {
        try {
            const response = await axios.get(
                `${BaseURL}user/post-generator/download/${postId}`,
                { headers: getAuthHeaders(), responseType: 'blob' }
            );
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `ppsk_post_${Date.now()}.png`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Error downloading:', error);
        }
    };

    const handleShare = async (imageUrl) => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'PPSK Post',
                    text: 'Check out my PPSK post!',
                    url: imageUrl,
                });
            } catch (error) {
                console.error('Error sharing:', error);
            }
        } else {
            navigator.clipboard.writeText(imageUrl);
            alert('Link copied to clipboard!');
        }
    };

    const getImageUrl = (path) => {
        if (!path) return '/placeholder-image.png';
        if (path.startsWith('http')) return path;
        return `${BucketURL}${path}`;
    };

    return (
        <Box sx={{ py: 6, bgcolor: '#f5f5f5', minHeight: '80vh' }}>
            <Container maxWidth="lg">
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                    <Box>
                        <Typography
                            variant="h4"
                            component="h1"
                            sx={{ fontWeight: 700, color: '#950904' }}
                        >
                            🎨 Post Generator
                        </Typography>
                        {userInfo && (
                            <Typography color="text.secondary">
                                Welcome, {userInfo.name || userInfo.firstName}!
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="My Posts">
                            <IconButton onClick={() => setHistoryOpen(true)}>
                                <HistoryIcon />
                            </IconButton>
                        </Tooltip>
                        {userInfo?.photo && (
                            <Avatar src={getImageUrl(userInfo.photo)} alt={userInfo.name} />
                        )}
                    </Box>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {/* Categories Section */}
                        {!selectedCategory ? (
                            <>
                                <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
                                    Select a Category
                                </Typography>
                                <Grid container spacing={3}>
                                    {categories.map((category) => (
                                        <Grid item xs={6} sm={4} md={3} key={category._id}>
                                            <Card
                                                sx={{
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s',
                                                    '&:hover': {
                                                        transform: 'translateY(-4px)',
                                                        boxShadow: 4,
                                                    },
                                                }}
                                                onClick={() => handleCategoryClick(category)}
                                            >
                                                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                                                    <Typography variant="h2" sx={{ mb: 1 }}>
                                                        {category.icon}
                                                    </Typography>
                                                    <Typography variant="h6" fontWeight="bold">
                                                        {category.name}
                                                    </Typography>
                                                    {category.nameGuj && (
                                                        <Typography variant="body2" color="text.secondary">
                                                            {category.nameGuj}
                                                        </Typography>
                                                    )}
                                                    <Chip
                                                        label={`${category.templateCount || 0} Templates`}
                                                        size="small"
                                                        sx={{ mt: 1 }}
                                                    />
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>

                                {/* Recent Posts */}
                                {myPosts.length > 0 && (
                                    <Box sx={{ mt: 6 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                            <Typography variant="h6" fontWeight="bold">
                                                📌 My Recent Posts
                                            </Typography>
                                            <Button onClick={() => setHistoryOpen(true)}>View All</Button>
                                        </Box>
                                        <Grid container spacing={2}>
                                            {myPosts.slice(0, 4).map((post) => (
                                                <Grid item xs={6} sm={3} key={post._id}>
                                                    <Card>
                                                        <CardMedia
                                                            component="img"
                                                            height="150"
                                                            image={getImageUrl(post.generatedImage)}
                                                            alt="Generated Post"
                                                            sx={{ objectFit: 'cover' }}
                                                        />
                                                        <CardActions sx={{ justifyContent: 'center' }}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDownload(post._id)}
                                                            >
                                                                <DownloadIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleShare(getImageUrl(post.generatedImage))}
                                                            >
                                                                <ShareIcon fontSize="small" />
                                                            </IconButton>
                                                        </CardActions>
                                                    </Card>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                )}
                            </>
                        ) : (
                            <>
                                {/* Templates Section */}
                                <Box sx={{ mb: 3 }}>
                                    <Button
                                        startIcon={<ArrowBackIcon />}
                                        onClick={() => { setSelectedCategory(null); setTemplates([]); setGeneratedPost(null); }}
                                    >
                                        Back to Categories
                                    </Button>
                                </Box>

                                <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
                                    {selectedCategory.icon} {selectedCategory.name} Templates
                                </Typography>

                                {loadingTemplates ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                                        <CircularProgress />
                                    </Box>
                                ) : templates.length > 0 ? (
                                    <Grid container spacing={3}>
                                        {templates.map((template) => (
                                            <Grid item xs={12} sm={6} md={4} key={template._id}>
                                                <Card
                                                    sx={{
                                                        cursor: 'pointer',
                                                        transition: 'all 0.3s',
                                                        '&:hover': {
                                                            transform: 'translateY(-4px)',
                                                            boxShadow: 4,
                                                        },
                                                    }}
                                                    onClick={() => handleTemplateClick(template)}
                                                >
                                                    <CardMedia
                                                        component="img"
                                                        height="250"
                                                        image={getImageUrl(template.previewImage)}
                                                        alt={template.name}
                                                        sx={{ objectFit: 'cover' }}
                                                    />
                                                    <CardContent>
                                                        <Typography variant="h6" fontWeight="bold">
                                                            {template.name}
                                                        </Typography>
                                                        {template.isDefault && (
                                                            <Chip label="Popular" size="small" color="primary" sx={{ mt: 1 }} />
                                                        )}
                                                    </CardContent>
                                                    <CardActions>
                                                        <Button
                                                            fullWidth
                                                            variant="contained"
                                                            sx={{ bgcolor: '#950904' }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedTemplate(template);
                                                                handleGenerate();
                                                            }}
                                                        >
                                                            Generate Post
                                                        </Button>
                                                    </CardActions>
                                                </Card>
                                            </Grid>
                                        ))}
                                    </Grid>
                                ) : (
                                    <Alert severity="info">No templates available in this category</Alert>
                                )}
                            </>
                        )}
                    </>
                )}
            </Container>

            {/* Generate Preview Dialog */}
            <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    {generating ? 'Generating Your Post...' : generatedPost ? 'Your Post is Ready!' : selectedTemplate?.name}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ textAlign: 'center' }}>
                        {generating ? (
                            <Box sx={{ py: 8 }}>
                                <CircularProgress size={60} />
                                <Typography sx={{ mt: 2 }}>Creating your personalized post...</Typography>
                            </Box>
                        ) : generatedPost ? (
                            <img
                                src={getImageUrl(generatedPost.imageUrl)}
                                alt="Generated Post"
                                style={{ maxWidth: '100%', maxHeight: '60vh' }}
                            />
                        ) : (
                            <>
                                <img
                                    src={getImageUrl(selectedTemplate?.previewImage)}
                                    alt={selectedTemplate?.name}
                                    style={{ maxWidth: '100%', maxHeight: '50vh' }}
                                />
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    Click "Generate Post" to create a personalized post with your photo and name!
                                </Alert>
                            </>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setPreviewOpen(false); setGeneratedPost(null); }}>
                        Close
                    </Button>
                    {generatedPost ? (
                        <>
                            <Button
                                variant="outlined"
                                startIcon={<ShareIcon />}
                                onClick={() => handleShare(getImageUrl(generatedPost.imageUrl))}
                            >
                                Share
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<DownloadIcon />}
                                onClick={() => handleDownload(generatedPost.id)}
                                sx={{ bgcolor: '#950904' }}
                            >
                                Download
                            </Button>
                        </>
                    ) : (
                        <Button
                            variant="contained"
                            onClick={handleGenerate}
                            disabled={generating}
                            sx={{ bgcolor: '#950904' }}
                        >
                            Generate Post
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* History Dialog */}
            <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>My Posts</DialogTitle>
                <DialogContent>
                    {myPosts.length > 0 ? (
                        <Grid container spacing={2}>
                            {myPosts.map((post) => (
                                <Grid item xs={6} sm={4} key={post._id}>
                                    <Card>
                                        <CardMedia
                                            component="img"
                                            height="150"
                                            image={getImageUrl(post.generatedImage)}
                                            alt="Generated Post"
                                        />
                                        <CardContent sx={{ py: 1 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                {post.category?.name || 'Post'}
                                            </Typography>
                                        </CardContent>
                                        <CardActions>
                                            <IconButton size="small" onClick={() => handleDownload(post._id)}>
                                                <DownloadIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => handleShare(getImageUrl(post.generatedImage))}>
                                                <ShareIcon fontSize="small" />
                                            </IconButton>
                                        </CardActions>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Alert severity="info">No posts generated yet. Create your first post!</Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHistoryOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MemberPostGenerator;
